import { DateTime } from "luxon";
import type { PbjPlanValue } from "../../../utils/models";

/**
 * Which attribute of a `PbjPlanValue` the tracker is currently showing.
 *
 * The endpoint ships every number in BOTH bases, so switching is an attribute read — never a
 * multiplication. One conversion factor cannot serve a per-day rate, a per-week rate and a
 * cumulative quarter total at once, because each divides by a different denominator.
 */
export type PlanBasis = "hours" | "hppd";

export const planValueOf = (value: PbjPlanValue, basis: PlanBasis): number => (basis === "hours" ? value.hours : value.hppd);

/**
 * Hour counts read as quantities (one decimal is enough for a figure in the hundreds); HPPD is a
 * small rate whose meaningful movement lives in the third decimal, matching the Prediction tab.
 */
export const formatPlanNumber = (value: number, basis: PlanBasis): string =>
	basis === "hours" ? value.toLocaleString(undefined, { maximumFractionDigits: 1 }) : value.toFixed(3);

export const formatPlanValue = (value: PbjPlanValue, basis: PlanBasis): string => formatPlanNumber(planValueOf(value, basis), basis);

/**
 * Whole hours, for the context chips (manual and estimated totals). Deliberately coarser than
 * `formatPlanNumber`: these sit beside the measured figures without summing to them, and a decimal
 * invites a reconciliation that was never on offer.
 */
export const formatWholeHours = (value: number): string => value.toLocaleString(undefined, { maximumFractionDigits: 0 });

export const formatCurrency = (value: number): string => value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/**
 * Dates on the progress payload are already Luxon `DateTime`s — the axios response interceptor
 * parses every ISO date in a GET body. Re-parsing one with `DateTime.fromISO` yields an Invalid
 * DateTime, so this takes the parsed value and only formats it.
 */
export const formatPlanDate = (value: DateTime): string => value.toLocaleString(DateTime.DATE_MED);

/**
 * Whether an action's on-track verdict carries any information yet.
 *
 * `onTrack` is `delivered >= requiredToDate`, and BOTH sides are charged over completed weeks only.
 * Before the first week closes `delivered` is exactly 0 (a sum over no weeks) and `requiredToDate` is
 * exactly 0 (`goalTotal x 0 / planDays`), so `0 >= 0` makes the verdict unconditionally true — a green
 * badge guaranteed for every feasible lever in week 0, however little is arriving. It reports the
 * absence of a requirement as success, which is how a card came to read "On track" beside a forecast
 * landing 10,820 hours short of its goal.
 *
 * The same reasoning the Investment panel withholds `onBudget` under: a verdict whose comparison is
 * vacuous is not shown, because a reader cannot tell a trivially-true one from an earned one.
 */
export const isActionJudged = (days: { covered: number }): boolean => days.covered > 0;

/**
 * Where a milestone sits on an action's progress bar, as a percentage of the committed lift.
 *
 * Positioned in HOURS, in both bases. Every hour the plan delivers lands inside the quarter, so
 * `milestone.hours` (what the edge costs) over `goalTotal.hours` (what the plan commits) is exactly
 * "what share of this commitment reaches that edge" — the same axis `progressPct` fills, which is
 * `deliveredToDate.hours / goalTotal.hours`.
 *
 * Reported HPPD is NOT interchangeable here, though it looks it. `goalTotal.hppd` is normalised over
 * the PLAN WINDOW's applicable days while `hppdGap` is the lift required across the whole QUARTER, so
 * the ratio came out scaled by `plan_days / quarter_days` and squeezed every marker toward the left:
 * on a plan saved seven weeks into a quarter, an edge costing 11,894 hours of a 12,169-hour
 * commitment was drawn at 46% instead of 98%, and three edges the plan cannot afford at all were
 * drawn comfortably inside it.
 *
 * Hours are used for the geometry in BOTH bases deliberately — the toggle changes which figure is
 * printed on the marker, never where the marker sits, so the bar cannot reflow when it is flipped.
 */
export const milestonePct = (milestoneHours: number, goalTotalHours: number): number =>
	goalTotalHours > 0 ? (milestoneHours / goalTotalHours) * 100 : Number.POSITIVE_INFINITY;
